import { createElement, useState } from 'react';
import { buttonVariants } from '@/components/ui/button/variants';
import { EmptyCircle } from '@/components/ui/empty-circle';
import { cn } from '@/lib/utils';
import { formatNotificationTime } from '@/lib/notifications/format-relative-time';
import { detailIcon, detailTone, notificationFacts, type DetailTone } from '@/lib/notifications/notification-detail-facts';
import { ICON_TONE_CLASSES, NOTIFICATION_TYPE_META } from '@/lib/notifications/notification-types';
import { marketDeepLinkId, metaString } from '@/lib/notifications/meta';
import { useOpenSignup } from '@/store/authModalStore';
import { useCustomInstance } from '@/lib/use-custom-instance';
import type { MeResponseDto } from '@/lib/models';
import type { PopupViewProps } from '../types';

/** Eyebrow (type label) color, by tone. */
const EYEBROW_TONE_CLASSES: Record<DetailTone, string> = {
  success: 'text-success-dark',
  danger: 'text-danger-dark',
  accent: 'text-accent',
  muted: 'text-muted',
};

/** Glow tint behind the medallion, by tone. */
const GLOW_TONE_CLASSES: Record<DetailTone, string> = {
  success: 'bg-success/25',
  danger: 'bg-danger/25',
  accent: 'bg-accent/25',
  muted: 'bg-accent/10',
};

/**
 * Generic detail template for the non-PRD notification types (announcement,
 * promotion, monetization, signup-reminder, comment-reply). This is the
 * pre-ARC-111 pop-up look — a tinted medallion (market image or type icon),
 * eyebrow + title + body, quick-glance fact chips from `meta`, and a market
 * deep-link CTA — ported to render inside the shared {@link PopupShell}. The 11
 * PRD types get their own Figma views instead.
 */
export function GenericPopup({ notification, onAction, onNavigate }: PopupViewProps) {
  const meta = notification.meta;
  const imageUrl = metaString(meta, 'marketImageUrl');
  const eventId = marketDeepLinkId(meta);
  const typeMeta = NOTIFICATION_TYPE_META[notification.type];
  const tone = detailTone(notification.type, meta);
  const facts = notificationFacts(meta);
  const [signupComplete, setSignupComplete] = useState(false);

  return (
    <>
      <div className="relative flex justify-center">
        <span aria-hidden className={cn('absolute inset-0 m-auto size-28 rounded-full blur-2xl', GLOW_TONE_CLASSES[tone])} />
        {typeMeta?.isMarket && imageUrl ? (
          <EmptyCircle
            src={imageUrl}
            alt=""
            className="relative size-24 shrink-0 rounded-full shadow-card ring-4 ring-surface"
          />
        ) : (
          <span
            aria-hidden
            className={cn(
              'relative flex size-24 shrink-0 items-center justify-center rounded-full shadow-card ring-4 ring-surface',
              ICON_TONE_CLASSES[tone],
            )}
          >
            {createElement(detailIcon(notification.type, meta), { className: 'size-10' })}
          </span>
        )}
      </div>

      <div className="flex flex-col items-center gap-3">
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.18em]', EYEBROW_TONE_CLASSES[tone])}>
          {typeMeta?.label ?? 'Notification'}
          <span className="font-medium normal-case tracking-normal text-muted">
            {' · '}
            {formatNotificationTime(notification.createdAt)}
          </span>
        </p>

        <h2 className="text-xl font-bold leading-snug text-primary">{notification.title}</h2>
        <p className="whitespace-pre-line text-sm leading-relaxed text-muted">
          {signupComplete ? 'Good news — your account is already fully set up. Nothing left to do here.' : notification.body}
        </p>

        {facts.length > 0 && (
          <ul className="mt-1 flex flex-wrap justify-center gap-2" data-testid="notification-facts">
            {facts.map((fact) => (
              <li
                key={fact.key}
                className="rounded-pill border border-border bg-surface-muted px-3 py-1 text-xs font-semibold text-primary"
              >
                {fact.label}
              </li>
            ))}
          </ul>
        )}
      </div>

      {eventId && (
        <button
          type="button"
          onClick={() => onAction('navigateMarket')}
          className={cn(buttonVariants({ variant: 'default', size: 'md' }), 'w-full')}
        >
          View market
        </button>
      )}

      {notification.type === 'SIGNUP_VERIFICATION_REMINDER' && (
        <SignupReminderCta onNavigate={onNavigate} complete={signupComplete} onComplete={() => setSignupComplete(true)} />
      )}
    </>
  );
}

/**
 * CTA for the post-signup verification reminder (ARC-145). The reminder may be
 * stale by the time it's tapped, so the click re-checks relevance against
 * `GET /auth/me` → `signupCompletedAt` before resuming the funnel; a stamped
 * value locks the button and swaps the body. Unchanged from the pre-dispatcher
 * detail pop-up.
 */
function SignupReminderCta({
  onNavigate,
  complete,
  onComplete,
}: {
  onNavigate: () => void;
  complete: boolean;
  onComplete: () => void;
}) {
  const openSignup = useOpenSignup();
  const request = useCustomInstance<MeResponseDto>();
  const [checking, setChecking] = useState(false);

  async function handleClick() {
    if (complete || checking) return;

    function resumeFunnel() {
      onNavigate();
      openSignup('notification');
    }

    setChecking(true);
    try {
      const me = await request({ url: '/auth/me', method: 'GET' });
      if (me.signupCompletedAt) onComplete();
      else resumeFunnel();
    } catch {
      resumeFunnel();
    } finally {
      setChecking(false);
    }
  }

  return (
    <button
      type="button"
      data-testid="notification-complete-signup-cta"
      disabled={complete || checking}
      onClick={() => void handleClick()}
      className={cn(buttonVariants({ variant: 'default', size: 'md' }), 'w-full')}
    >
      {complete ? 'Account fully set up' : checking ? 'Checking…' : 'Complete Sign Up'}
    </button>
  );
}
